import React, { useEffect, useMemo, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import styled from "styled-components";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslation } from "react-i18next";
import { Percent, Clock, Maximize2, Minimize2 } from "lucide-react";

import heatwaveStationMapCsv from "../../../data/HeatwaveJune2026/station_delay_heatwave_map.csv";
import {
  countryNameFromUic,
  formatMultiplierChangePct,
  HeatwaveStationMapRow,
  MIN_STATION_SAMPLE_SIZE,
  parseHeatwaveStationMapCsv,
} from "./heatwaveChartShared";

export type HeatwaveMapStation = HeatwaveStationMapRow & {
  id: string;
  country: string;
  lng: number;
  lat: number;
};

const colors = {
  textDark: "#0f2744",
  textMuted: "#5a7a94",
  cardBorder: "rgba(255, 255, 255, 0.95)",
  cardShadow: "rgba(74, 159, 212, 0.15)",
  greyNeutral: "#D1D5DB",
  skyDeep: "#4A9FD4",
  yellow400: "#FACC15",
  orange500: "#F97316",
  red600: "#DC2626",
};

const MULTIPLIER_COLOR_NEUTRAL = 1;
const MULTIPLIER_COLOR_MAX = 5;
const WARM_YELLOW_T = 0.08;
// Full orange is reached later than +100% (multiplier 2, t = 0.25) so that point stays yellower.
const WARM_ORANGE_T = 0.35;
const LIGHT_STYLE = "mapbox://styles/mapbox/light-v11";
const SOURCE_ID = "heatwave-stations";
const LAYER_ID = "heatwave-stations-circles";
const HOVER_LAYER_ID = "heatwave-stations-hover";
const CIRCLE_RADIUS_MIN = 3.5;
const CIRCLE_RADIUS_MAX = 9;
const HOVER_RADIUS_BONUS = 2;
const ABS_COLOR_MIN_S = -3 * 60; // -3 minutes, floor for absolute mode
const ABS_COLOR_MAX_S = 10 * 60; // +10 minutes, ceiling for absolute mode

type ColorMode = "relative" | "minutes";

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 500px;
  height: 500px;
  border-radius: 1rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 8px 32px ${colors.cardShadow};
  isolation: isolate;
  contain: layout style paint;
  transform: translateZ(0);

  @media (min-width: 768px) {
    min-height: 600px;
    height: 600px;
  }

  &:fullscreen,
  &:-webkit-full-screen,
  &:-moz-full-screen {
    border-radius: 0;
    height: 100dvh;
    min-height: 100dvh;
  }
`;

const FullscreenButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(126, 200, 227, 0.55);
  border-radius: 50%;
  box-shadow: 0 4px 16px ${colors.cardShadow};
  cursor: pointer;
  color: ${colors.textMuted};
  transition: color 0.15s ease, background 0.15s ease;
  padding: 0;

  &:hover {
    color: ${colors.textDark};
    background: white;
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  z-index: 1;

  .mapboxgl-map,
  .mapboxgl-canvas {
    border-radius: 1rem;
    width: 100%;
    height: 100%;
  }

  .mapboxgl-ctrl button {
    background-color: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(126, 200, 227, 0.6);
  }

  .mapboxgl-ctrl-compass {
    display: none !important;
  }
`;

const Legend = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(126, 200, 227, 0.55);
  border-radius: 0.75rem;
  padding: 0.75rem 0.875rem;
  box-shadow: 0 4px 16px ${colors.cardShadow};
  font-size: 0.75rem;
  color: ${colors.textMuted};
  max-width: calc(100% - 24px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const LegendGradient = styled.div<{ $background: string }>`
  width: 100%;
  height: 0.625rem;
  border-radius: 9999px;
  margin: 0.4rem 0 0.35rem;
  background: ${({ $background }) => $background};
`;

const LegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 0.6875rem;
  color: ${colors.textMuted};
`;

const TooltipContainer = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  transform: translate(-50%, calc(-100% - 12px));
  pointer-events: none;
  z-index: 1000;
`;

const Tooltip = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid ${colors.cardBorder};
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 20px rgba(74, 159, 212, 0.2);
  font-size: 0.875rem;
  color: ${colors.textDark};
  text-align: left;
  min-width: 10rem;
  animation: tooltipPopFade 0.15s ease-out;

  @keyframes tooltipPopFade {
    from { opacity: 0; transform: scale(0.94); }
    to   { opacity: 1; transform: scale(1); }
  }
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.375rem;
  color: ${colors.textDark};
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.8125rem;

  &:not(:last-child) {
    margin-bottom: 0.35rem;
  }

  span:first-child {
    color: ${colors.textMuted};
  }

  span:last-child {
    font-weight: 600;
    color: ${colors.textDark};
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 500px;
  color: ${colors.textMuted};
  font-size: 0.9375rem;

  @media (min-width: 768px) {
    min-height: 600px;
  }
`;

const MapModeSwitch = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  display: flex;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(126, 200, 227, 0.55);
  border-radius: 9999px;
  padding: 2px;
  box-shadow: 0 4px 16px ${colors.cardShadow};
  gap: 2px;
`;

const MapModeSwitchBtn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  color: ${({ $active }) => ($active ? colors.textDark : colors.textMuted)};
  background: ${({ $active }) => ($active ? "white" : "transparent")};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${({ $active }) => ($active ? "0 1px 4px rgba(0,0,0,0.12)" : "none")};
  white-space: nowrap;

  &:hover {
    color: ${colors.textDark};
  }
`;

function parseHexColor(hex: string): [number, number, number] {
  const normalized = hex.replace("#", "");
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
}

function lerpColor(from: string, to: string, t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const [r1, g1, b1] = parseHexColor(from);
  const [r2, g2, b2] = parseHexColor(to);
  const r = Math.round(r1 + (r2 - r1) * clamped);
  const g = Math.round(g1 + (g2 - g1) * clamped);
  const b = Math.round(b1 + (b2 - b1) * clamped);
  return `rgb(${r}, ${g}, ${b})`;
}

function getMultiplierColor(multiplier: number, colorMin: number, colorMax: number): string {
  if (multiplier <= MULTIPLIER_COLOR_NEUTRAL) {
    const range = MULTIPLIER_COLOR_NEUTRAL - colorMin;
    const t = range > 0 ? (multiplier - colorMin) / range : 0;
    return lerpColor(colors.skyDeep, colors.greyNeutral, t);
  }

  const clamped = Math.min(multiplier, colorMax);
  const range = colorMax - MULTIPLIER_COLOR_NEUTRAL;
  const t = range > 0 ? (clamped - MULTIPLIER_COLOR_NEUTRAL) / range : 0;

  if (t <= WARM_YELLOW_T) {
    return lerpColor(colors.greyNeutral, colors.yellow400, t / WARM_YELLOW_T);
  }
  if (t <= WARM_ORANGE_T) {
    return lerpColor(
      colors.yellow400,
      colors.orange500,
      (t - WARM_YELLOW_T) / (WARM_ORANGE_T - WARM_YELLOW_T)
    );
  }
  return lerpColor(
    colors.orange500,
    colors.red600,
    (t - WARM_ORANGE_T) / (1 - WARM_ORANGE_T)
  );
}

function buildLegendGradientBackground(
  colorMin: number,
  colorMax: number,
  steps = 32
): string {
  const stops = Array.from({ length: steps + 1 }, (_, index) => {
    const position = (index / steps) * 100;
    const multiplier = colorMin + (index / steps) * (colorMax - colorMin);
    const color = getMultiplierColor(multiplier, colorMin, colorMax);
    return `${color} ${position}%`;
  });

  return `linear-gradient(to right, ${stops.join(", ")})`;
}

function formatDelaySeconds(seconds: number): string {
  return `${(seconds / 60).toFixed(1)} min`;
}

function getAbsoluteDelayDiff(station: HeatwaveMapStation): number {
  return station.avgDelayAbove30S - station.avgDelayBelow30S;
}

function getAbsoluteColor(diffS: number): string {
  if (diffS <= 0) {
    const t = Math.max(0, Math.min(1, diffS / ABS_COLOR_MIN_S));
    return lerpColor(colors.greyNeutral, colors.skyDeep, t);
  }
  const clamped = Math.min(diffS, ABS_COLOR_MAX_S);
  const t = clamped / ABS_COLOR_MAX_S;
  if (t <= WARM_YELLOW_T) {
    return lerpColor(colors.greyNeutral, colors.yellow400, t / WARM_YELLOW_T);
  }
  if (t <= WARM_ORANGE_T) {
    return lerpColor(
      colors.yellow400,
      colors.orange500,
      (t - WARM_YELLOW_T) / (WARM_ORANGE_T - WARM_YELLOW_T)
    );
  }
  return lerpColor(
    colors.orange500,
    colors.red600,
    (t - WARM_ORANGE_T) / (1 - WARM_ORANGE_T)
  );
}

function buildAbsoluteLegendGradient(steps = 32): string {
  const range = ABS_COLOR_MAX_S - ABS_COLOR_MIN_S;
  const stops = Array.from({ length: steps + 1 }, (_, index) => {
    const position = (index / steps) * 100;
    const diffS = ABS_COLOR_MIN_S + (index / steps) * range;
    return `${getAbsoluteColor(diffS)} ${position}%`;
  });
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

function formatDelayDiffMin(diffS: number): string {
  const min = diffS / 60;
  const sign = min >= 0 ? "+" : "";
  return `${sign}${min.toFixed(1)} min`;
}

function getMultiplierColorRange(stations: HeatwaveMapStation[]): {
  min: number;
  max: number;
  colorMin: number;
  colorMax: number;
} {
  const colorMax = MULTIPLIER_COLOR_MAX;

  if (stations.length === 0) {
    return {
      min: 0,
      max: colorMax,
      colorMin: 0,
      colorMax,
    };
  }

  const multipliers = stations.map((station) => station.delayMultiplier);
  const min = Math.min(...multipliers);
  const max = Math.max(...multipliers);
  const colorMin = min;

  return { min, max, colorMin, colorMax };
}

function getStationSampleCount(station: HeatwaveMapStation): number {
  return station.nBelow + station.nAbove;
}

function getCircleRadius(
  sampleCount: number,
  minSamples: number,
  maxSamples: number
): number {
  if (maxSamples <= minSamples) {
    return (CIRCLE_RADIUS_MIN + CIRCLE_RADIUS_MAX) / 2;
  }

  const t =
    (Math.log(sampleCount) - Math.log(minSamples)) /
    (Math.log(maxSamples) - Math.log(minSamples));
  const clamped = Math.max(0, Math.min(1, t));
  return CIRCLE_RADIUS_MIN + clamped * (CIRCLE_RADIUS_MAX - CIRCLE_RADIUS_MIN);
}

function stationToFeature(
  station: HeatwaveMapStation,
  minSamples: number,
  maxSamples: number,
  color: string
): GeoJSON.Feature<GeoJSON.Point> {
  const sampleCount = getStationSampleCount(station);
  const circleRadius = getCircleRadius(sampleCount, minSamples, maxSamples);

  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [station.lng, station.lat],
    },
    properties: {
      id: station.id,
      name: station.name,
      country: station.country,
      avgDelayBelow30S: station.avgDelayBelow30S,
      avgDelayAbove30S: station.avgDelayAbove30S,
      delayMultiplier: station.delayMultiplier,
      nBelow: station.nBelow,
      nAbove: station.nAbove,
      sampleCount,
      circleRadius,
      color,
    },
  };
}

function featureToStation(
  feature: GeoJSON.Feature<GeoJSON.Point>
): HeatwaveMapStation | null {
  const props = feature.properties;
  if (!props) return null;

  const [lng, lat] = feature.geometry.coordinates;
  return {
    id: String(props.id),
    uic: String(props.id),
    name: String(props.name),
    country: String(props.country),
    lng,
    lat,
    latitude: lat,
    longitude: lng,
    avgDelayBelow30S: Number(props.avgDelayBelow30S),
    avgDelayAbove30S: Number(props.avgDelayAbove30S),
    delayMultiplier: Number(props.delayMultiplier),
    nBelow: Number(props.nBelow) || 0,
    nAbove: Number(props.nAbove) || 0,
  };
}

function removeStationLayers(map: mapboxgl.Map) {
  if (map.getLayer(HOVER_LAYER_ID)) map.removeLayer(HOVER_LAYER_ID);
  if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

function addStationLayers(map: mapboxgl.Map, data: GeoJSON.FeatureCollection) {
  map.addSource(SOURCE_ID, {
    type: "geojson",
    data,
    promoteId: "id",
  });

  map.addLayer({
    id: LAYER_ID,
    type: "circle",
    source: SOURCE_ID,
    paint: {
      "circle-radius": ["get", "circleRadius"],
      "circle-color": ["get", "color"],
      "circle-opacity": 1,
    },
  });

  map.addLayer({
    id: HOVER_LAYER_ID,
    type: "circle",
    source: SOURCE_ID,
    filter: ["==", ["get", "id"], ""],
    paint: {
      "circle-radius": ["+", ["get", "circleRadius"], HOVER_RADIUS_BONUS],
      "circle-color": ["get", "color"],
      "circle-opacity": 1,
      "circle-stroke-width": 2,
      "circle-stroke-color": "rgba(15, 39, 68, 0.35)",
    },
  });
}

interface HeatwaveStationMapProps {
  visible?: boolean;
}

const HeatwaveStationMap: React.FC<HeatwaveStationMapProps> = ({ visible = true }) => {
  const { t } = useTranslation();
  const [stations, setStations] = useState<HeatwaveMapStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredStation, setHoveredStation] = useState<HeatwaveMapStation | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [colorMode, setColorMode] = useState<ColorMode>("relative");
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const layersReadyRef = useRef(false);
  const initStartedRef = useRef(false);
  const hoverMoveHandlerRef = useRef<((event: mapboxgl.MapMouseEvent) => void) | null>(
    null
  );
  const hoverLeaveHandlerRef = useRef<(() => void) | null>(null);

  const mapboxAccessToken = useMemo(
    () =>
      process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
      "pk.eyJ1IjoiYXJuZW55c2NodXVjaHV1IiwiYSI6ImNtaXluODR3ZTBldnkzZ3NkODE2cWh6MzkifQ.ekhgFoHug_qEgoETJmuPXA",
    []
  );

  const multiplierColorRange = useMemo(
    () => getMultiplierColorRange(stations),
    [stations]
  );

  const legendGradientBackground = useMemo(
    () =>
      colorMode === "minutes"
        ? buildAbsoluteLegendGradient()
        : buildLegendGradientBackground(
            multiplierColorRange.colorMin,
            multiplierColorRange.colorMax
          ),
    [colorMode, multiplierColorRange.colorMin, multiplierColorRange.colorMax]
  );

  const stationGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    if (stations.length === 0) {
      return { type: "FeatureCollection", features: [] };
    }

    const sampleCounts = stations.map(getStationSampleCount);
    const minSamples = Math.max(MIN_STATION_SAMPLE_SIZE, Math.min(...sampleCounts));
    const maxSamples = Math.max(...sampleCounts);

    const sorted = [...stations].sort((a, b) =>
      colorMode === "minutes"
        ? getAbsoluteDelayDiff(a) - getAbsoluteDelayDiff(b)
        : a.delayMultiplier - b.delayMultiplier
    );

    return {
      type: "FeatureCollection",
      features: sorted.map((station) => {
        const color =
          colorMode === "minutes"
            ? getAbsoluteColor(getAbsoluteDelayDiff(station))
            : getMultiplierColor(
                station.delayMultiplier,
                multiplierColorRange.colorMin,
                multiplierColorRange.colorMax
              );
        return stationToFeature(station, minSamples, maxSamples, color);
      }),
    };
  }, [stations, multiplierColorRange, colorMode]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(heatwaveStationMapCsv);
        if (!response.ok) {
          setStations([]);
          return;
        }
        const parsed = parseHeatwaveStationMapCsv(await response.text());
        setStations(
          parsed.map((row) => ({
            ...row,
            id: row.uic,
            country: countryNameFromUic(row.uic, t),
            lng: row.longitude,
            lat: row.latitude,
          }))
        );
      } catch {
        setStations([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  useEffect(() => {
    if (loading || stations.length === 0) return;
    if (!mapContainerRef.current || mapRef.current || initStartedRef.current) return;
    if (!mapboxAccessToken) return;

    initStartedRef.current = true;
    mapboxgl.accessToken = mapboxAccessToken;

    let cancelled = false;

    const tryInit = (attempt: number) => {
      if (cancelled || !mapContainerRef.current || mapRef.current) return;

      if (
        mapContainerRef.current.offsetWidth === 0 ||
        mapContainerRef.current.offsetHeight === 0
      ) {
        if (attempt < 20) {
          setTimeout(() => tryInit(attempt + 1), 50);
        } else {
          initStartedRef.current = false;
        }
        return;
      }

      try {
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: LIGHT_STYLE,
          center: [10.5, 48.0],
          zoom: 4,
          attributionControl: false,
          renderWorldCopies: false,
        });

        map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
        map.on("load", () => {
          setMapLoaded(true);
          map.resize();
        });
        map.on("error", (event) => {
          console.error("Heatwave map error:", event);
        });
        map.on("dragend", () => {
          const { lng, lat } = map.getCenter();
          mixpanel.track("Heatwave Map Panned", {
            zoom: Math.round(map.getZoom() * 10) / 10,
            lng: Math.round(lng * 10) / 10,
            lat: Math.round(lat * 10) / 10,
          });
        });

        mapRef.current = map;
        setTimeout(() => mapRef.current?.resize(), 100);
      } catch (error) {
        console.error("Failed to initialize heatwave map:", error);
        initStartedRef.current = false;
      }
    };

    tryInit(0);

    return () => {
      cancelled = true;
      initStartedRef.current = false;
      layersReadyRef.current = false;
      setMapLoaded(false);
      if (mapRef.current && hoverMoveHandlerRef.current) {
        mapRef.current.off("mousemove", hoverMoveHandlerRef.current);
      }
      if (mapRef.current && hoverLeaveHandlerRef.current) {
        mapRef.current.off("mouseleave", hoverLeaveHandlerRef.current);
      }
      hoverMoveHandlerRef.current = null;
      hoverLeaveHandlerRef.current = null;
      if (mapRef.current) {
        removeStationLayers(mapRef.current);
        mapRef.current.remove();
      }
      mapRef.current = null;
    };
  }, [mapboxAccessToken, loading, stations.length]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !visible) return;

    mapRef.current.resize();
    const frame = requestAnimationFrame(() => mapRef.current?.resize());
    return () => cancelAnimationFrame(frame);
  }, [visible, mapLoaded]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !visible || stations.length === 0) return;

    const map = mapRef.current;

    const syncLayers = () => {
      if (!layersReadyRef.current) {
        addStationLayers(map, stationGeoJson);
        layersReadyRef.current = true;
      } else {
        const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
        source?.setData(stationGeoJson);
      }
    };

    if (map.loaded()) {
      syncLayers();
    } else {
      map.once("load", syncLayers);
    }
  }, [stationGeoJson, mapLoaded, visible, stations.length]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !visible || !layersReadyRef.current) return;

    const map = mapRef.current;

    if (hoverMoveHandlerRef.current) {
      map.off("mousemove", hoverMoveHandlerRef.current);
    }
    if (hoverLeaveHandlerRef.current) {
      map.off("mouseleave", hoverLeaveHandlerRef.current);
    }

    const handleMouseMove = (event: mapboxgl.MapMouseEvent) => {
      const features = map.queryRenderedFeatures(event.point, { layers: [LAYER_ID] });
      if (!features.length) {
        map.getCanvas().style.cursor = "";
        setHoveredStation(null);
        setTooltipPosition(null);
        map.setFilter(HOVER_LAYER_ID, ["==", ["get", "id"], ""]);
        return;
      }

      const station = featureToStation(features[0] as GeoJSON.Feature<GeoJSON.Point>);
      if (!station) return;

      map.getCanvas().style.cursor = "pointer";
      setHoveredStation(station);
      setTooltipPosition({ x: event.point.x, y: event.point.y });
      map.setFilter(HOVER_LAYER_ID, ["==", ["get", "id"], station.id]);
    };

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = "";
      setHoveredStation(null);
      setTooltipPosition(null);
      map.setFilter(HOVER_LAYER_ID, ["==", ["get", "id"], ""]);
    };

    hoverMoveHandlerRef.current = handleMouseMove;
    hoverLeaveHandlerRef.current = handleMouseLeave;
    map.on("mousemove", handleMouseMove);
    map.on("mouseleave", handleMouseLeave);

    return () => {
      map.off("mousemove", handleMouseMove);
      map.off("mouseleave", handleMouseLeave);
      hoverMoveHandlerRef.current = null;
      hoverLeaveHandlerRef.current = null;
    };
  }, [mapLoaded, visible, stations.length]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Give the browser time to resize the element, then tell Mapbox
      setTimeout(() => mapRef.current?.resize(), 150);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.().catch(() => {});
      mixpanel.track("Heatwave Map Fullscreen Opened");
    } else {
      document.exitFullscreen?.();
      mixpanel.track("Heatwave Map Fullscreen Closed");
    }
  };

  if (loading) {
    return <LoadingState>{t("heatwave.map.loading")}</LoadingState>;
  }

  if (!stations.length) {
    return <LoadingState>{t("heatwave.map.loadError")}</LoadingState>;
  }

  return (
    <MapContainer ref={containerRef}>
      <MapWrapper ref={mapContainerRef} />
      <FullscreenButton
        onClick={toggleFullscreen}
        title={isFullscreen ? t("heatwave.map.exitFullscreen") : t("heatwave.map.enterFullscreen")}
        aria-label={isFullscreen ? t("heatwave.map.exitFullscreen") : t("heatwave.map.enterFullscreen")}
      >
        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
      </FullscreenButton>
      <MapModeSwitch>
        <MapModeSwitchBtn
          $active={colorMode === "relative"}
          onClick={() => { mixpanel.track("Heatwave Map Mode Switched", { mode: "relative" }); setColorMode("relative"); }}
        >
          <Percent size={14} />
          {t("heatwave.map.switchRelative")}
        </MapModeSwitchBtn>
        <MapModeSwitchBtn
          $active={colorMode === "minutes"}
          onClick={() => { mixpanel.track("Heatwave Map Mode Switched", { mode: "minutes" }); setColorMode("minutes"); }}
        >
          <Clock size={14} />
          {t("heatwave.map.switchMinutes")}
        </MapModeSwitchBtn>
      </MapModeSwitch>
      <Legend>
        <div>
          {colorMode === "minutes"
            ? t("heatwave.map.legendTitleMinutes")
            : t("heatwave.map.legendTitle")}
        </div>
        <LegendGradient $background={legendGradientBackground} aria-hidden />
        <LegendLabels>
          {colorMode === "minutes" ? (
            <>
              <span>−3 min</span>
              <span>+10 min</span>
            </>
          ) : (
            <>
              <span>{formatMultiplierChangePct(multiplierColorRange.min)}</span>
              <span>{formatMultiplierChangePct(MULTIPLIER_COLOR_MAX)}</span>
            </>
          )}
        </LegendLabels>
      </Legend>
      {hoveredStation && tooltipPosition && (
        <TooltipContainer $x={tooltipPosition.x} $y={tooltipPosition.y}>
          <Tooltip>
            <TooltipTitle>{hoveredStation.name}</TooltipTitle>
            <TooltipRow>
              <span>{t("heatwave.map.delayBelow30")}</span>
              <span>{formatDelaySeconds(hoveredStation.avgDelayBelow30S)}</span>
            </TooltipRow>
            <TooltipRow>
              <span>{t("heatwave.map.delayAbove30")}</span>
              <span>{formatDelaySeconds(hoveredStation.avgDelayAbove30S)}</span>
            </TooltipRow>
            <TooltipRow>
              <span>
                {colorMode === "minutes"
                  ? t("heatwave.map.delayDiff")
                  : t("heatwave.map.delayMultiplier")}
              </span>
              <span>
                {colorMode === "minutes"
                  ? formatDelayDiffMin(getAbsoluteDelayDiff(hoveredStation))
                  : formatMultiplierChangePct(hoveredStation.delayMultiplier)}
              </span>
            </TooltipRow>
          </Tooltip>
        </TooltipContainer>
      )}
    </MapContainer>
  );
};

export default React.memo(HeatwaveStationMap);
